import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {rejectPendingIssueSuggestionsMutation as queryId} from '@github-ui/server-persisted-queries'
import type {RejectPendingIssueSuggestionsMutationMutation} from '@github-ui/server-persisted-queries/types/rejectPendingIssueSuggestionsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable} from 'relay-runtime'

import {
  type PendingSuggestionMutationOptions,
  pendingSuggestionOperation,
  type SuggestionRef,
  toSuggestionInput,
  updateRelayPendingSuggestions,
} from '../utils/suggestion-ref'
import {issueLifecyclePath} from './issue-lifecycle-mutation'

export type RejectSuggestionRef = SuggestionRef
type RejectPendingIssueSuggestionsVariables = {
  operation: ReturnType<typeof pendingSuggestionOperation>
  actorId: string
  suggestions: SuggestionRef[]
  onRejected?: () => void
}

export function useRejectPendingIssueSuggestions({
  issueId,
  owner,
  repository,
  issueNumber,
  onCompleted,
  onError,
}: PendingSuggestionMutationOptions<RejectPendingIssueSuggestionsMutationMutation>) {
  const environment = useRelayEnvironment()
  const {mutate} = useMutation<
    RejectPendingIssueSuggestionsMutationMutation,
    Error,
    RejectPendingIssueSuggestionsVariables,
    Disposable
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({actorId, suggestions}) => {
      const path = `${issueLifecyclePath(owner, repository, issueNumber)}/pending_suggestions.json`
      const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId})}`, {
        method: 'DELETE',
        body: {actorId, suggestions: suggestions.map(toSuggestionInput)},
      })
      if (!response.ok) throw new Error(`Could not decline suggestions (${response.status})`)
      const json = (await response.json()) as {
        data?: RejectPendingIssueSuggestionsMutationMutation
        errors?: Array<{message?: string}>
      }
      // Preserve Relay's distinction between GraphQL errors with data and a failed request.
      if (!json.data?.rejectPendingIssueSuggestions) {
        throw new Error(json.errors?.[0]?.message ?? 'Could not decline suggestions')
      }
      return json.data
    },
    onMutate: ({operation, onRejected}) => {
      const optimisticUpdate = environment.applyUpdate({
        storeUpdater: store => updateRelayPendingSuggestions(store, operation),
      })
      onRejected?.()
      return optimisticUpdate
    },
    onSuccess: async (data, {operation}) => {
      await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
      commitLocalUpdate(environment, store => updateRelayPendingSuggestions(store, operation))
      onCompleted?.(data)
    },
    onError: error => onError?.(error),
    onSettled: (_data, _error, _variables, optimisticUpdate) => optimisticUpdate?.dispose(),
  })

  return useCallback(
    (actorId: string, suggestions: SuggestionRef[], onRejected?: () => void) => {
      const operation = pendingSuggestionOperation(issueId, {
        suggestionIds: suggestions.map(suggestion => suggestion.id),
        state: 'REJECTED',
      })
      mutate({operation, actorId, suggestions, onRejected})
    },
    [issueId, mutate],
  )
}
